import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import { ConfigContext } from '@/components/ConfigProvider';
import { enforceEx } from '@/hooks/useAuth';
import useTenants from '@/hooks/useTenants';

import useClusters from '../useClusters';
import { isPortalNavPath } from '../useRouteScope';
import { getClusterMenu } from './clusterMenu';
import { getDataCenterMenu } from './dataCenter';
import { personalMenu } from './personal';
import { useProductMenu } from './productMenu';
import { buildSubCenterLeftMenu, getEffectiveSubCenter, stripSubCenterFromPathname } from './subCenter';
import { systemMenu } from './systemMenu';
import { resolveSystemMenuConfig } from './systemMenuAccess';
import { getTenantMenu } from './tenantMenu';

/** 空菜单的稳定引用。勿对本数组做 push 等原地修改；仅作「无菜单」占位。 */
const STABLE_EMPTY_MENU: any[] = [];

// 根据 auth 和后台菜单配置过滤菜单；pathToInfo 供 useCurrentMenu 快速解析标题
//
// 依赖与死循环：各 loadMenu 仅由 useCallback / useEffect 声明的依赖（policies、backMenu、cluster、pathname、
// params.tenant / params.cluster 等）变化而重新执行；执行结束后至多一次 setState，不会在同步阶段连环触发自身。
// 若业务侧每帧传入新的 backMenu、policies 引用，会表现为频繁重新拉菜单，而非 React 意义上的「无限更新」；
// 租户/顶部无菜单时使用 STABLE_EMPTY_MENU，避免 useLeftMenu 的 useMemo 因字面量 [] 引用每帧变化而整树重算。
const pathToInfo: Record<string, string> = {};

type ViewAuthTuple = [string, string, string];

/** 校验菜单 viewAuth（及可选附加权限）是否全部通过 */
async function matchViewAuths(
  viewAuth: ViewAuthTuple,
  viewAuthExtra: ViewAuthTuple[] | undefined,
  policies,
  datacenter?: string,
  workspace?: string,
) {
  const auths = [viewAuth, ...(viewAuthExtra || [])];
  for (const [group, resource, verb] of auths) {
    const [okEx] = await enforceEx(policies, '*', datacenter || '-', workspace || '-', group, '*', resource, verb);
    if (!okEx) return false;
  }
  return true;
}

async function filterAuthMenu({ data, policies, backMenu, parent = null, num = 1, datacenter = undefined, workspace = undefined }) {
  const menu = formatMenuData(data, parent?.permissionChildrenKey || parent?.key);
  if (!menu?.length) return menu;
  const arr = [];
  for (const obj of menu) {
    // 新接入 RBAC 的历史叶子可沿用已启用的父级 MODULES，最终仍需通过自身 Casbin 权限。
    const visibleWithParent = obj.permissionVisibleWithParent && parent && backMenu.includes(parent.key);
    if ((!backMenu.includes(obj.key) && !visibleWithParent && !obj.alwaysVisible) || obj.hidden) {
      obj.path && (pathToInfo[obj.path] = obj.title);
      continue;
    }
    obj.parent = parent;
    const hadChildren = !!obj.children?.length;
    obj.children = await filterAuthMenu({
      data: obj.children,
      policies,
      backMenu,
      parent: obj,
      num: num + 1,
      datacenter,
      workspace,
    });

    // 可见性：白名单已通过 → 再按权限 / 子级结果决定是否展示
    // 保留：上下级均在 MODULES 中，但子级全部无权限（且无「无需权限」的可见叶子）时，上级也不展示
    let flag = false;
    if (obj?.viewAuth) {
      flag = await matchViewAuths(obj.viewAuth, obj.viewAuthExtra, policies, datacenter, workspace);
      pathToInfo[obj.path] = obj.title;
      // 曾有子级但过滤后为空：不展示空壳上级（即使自身 viewAuth 通过）
      if (hadChildren && !obj.children?.length) {
        flag = false;
      }
    } else if (hadChildren) {
      // 分组：仅当仍有可见子级时展示
      flag = obj.children.length > 0;
      if (flag) {
        obj.path = num < 3 ? undefined : obj.path || obj?.children?.[0]?.path;
      }
    } else {
      // 无需权限的叶子：MODULES 通过即可展示
      flag = true;
      obj.path && (pathToInfo[obj.path] = obj.title);
    }
    if (flag) {
      arr.push(obj);
    }
  }
  return arr;
}

// 格式化菜单数据，和后台菜单 key 对应上
function formatMenuData(data, parentKey = '') {
  if (!data?.length) return [];
  return data.map((item) => {
    const key = parentKey ? `${parentKey}-${item.title}` : item.title;
    return {
      ...item,
      key,
    };
  });
}

// 数据中心菜单
function useDataCenterMenu({ backMenu, policies }) {
  const { cluster } = useClusters();
  const [menu, setMenu] = useState<any>([]);

  const loadMenu = useCallback(async () => {
    const arr = await filterAuthMenu({
      data: getDataCenterMenu(cluster),
      policies,
      backMenu,
      datacenter: cluster,
    });
    setMenu(arr);
  }, [policies, cluster, backMenu]);

  useEffect(() => {
    void loadMenu();
  }, [loadMenu]);

  return menu;
}

/** 返回租户过滤菜单及是否仍在按最新权限/租户上下文计算。 */
export function useTenantMenuState() {
  const { backMenu, policies, tenant: ctxTenant, tenantCluster: ctxCluster, tenantList, tenantClusters } = useContext(ConfigContext);
  const params = useParams();
  const workspace = params.tenant || ctxTenant;
  const cluster = params.cluster || ctxCluster || tenantClusters?.[0]?.name;
  const [result, setResult] = useState<{
    menu: any[] | null;
    backMenu: string[];
    policies: string[][];
    workspace?: string;
    cluster?: string;
  }>({ menu: null, backMenu, policies });

  useEffect(() => {
    if (!tenantList?.length || !workspace) {
      setResult({ menu: [], backMenu, policies, workspace, cluster });
      return;
    }
    let cancelled = false;
    void filterAuthMenu({
      data: getTenantMenu(workspace, cluster),
      policies,
      backMenu,
      workspace,
    }).then((arr) => {
      if (!cancelled) setResult({ menu: arr, backMenu, policies, workspace, cluster });
    });
    return () => {
      cancelled = true;
    };
  }, [policies, backMenu, workspace, cluster, tenantList?.length]);

  return {
    menu: result.menu ?? [],
    loading: result.backMenu !== backMenu || result.policies !== policies || result.workspace !== workspace || result.cluster !== cluster,
  };
}

// 租户菜单
function useTenantMenu() {
  const { tenantList } = useTenants();
  const { menu } = useTenantMenuState();
  return tenantList?.length > 0 ? menu : STABLE_EMPTY_MENU;
}

// 集群菜单（getClusterMenu 依赖当前 URL，故以 pathname 为依赖；不用 useCallback 以免与「非常量」数据源冲突）
function useClusterMenu({ policies, backMenu }) {
  const { pathname } = useLocation();
  const [menu, setMenu] = useState<any>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const arr = await filterAuthMenu({
        data: getClusterMenu(),
        policies,
        backMenu,
      });
      if (!cancelled) setMenu(arr);
    })();
    return () => {
      cancelled = true;
    };
  }, [policies, backMenu, pathname]);

  return menu;
}

/** 返回系统菜单及其是否仍在按最新权限数据计算。 */
export function useSystemMenuState() {
  const { backMenu, policies } = useContext(ConfigContext); // 后台菜单配置权限
  const [result, setResult] = useState<any>({ menu: null });

  useEffect(() => {
    let cancelled = false;
    void filterAuthMenu({ data: systemMenu, policies, backMenu: resolveSystemMenuConfig(backMenu, policies) }).then((arr) => {
      if (!cancelled) setResult({ menu: arr?.[0], backMenu, policies });
    });
    return () => {
      cancelled = true;
    };
  }, [policies, backMenu]);

  return {
    menu: result.menu,
    loading: result.backMenu !== backMenu || result.policies !== policies,
  };
}

// 系统菜单
export function useSystemMenu() {
  return useSystemMenuState().menu;
}

// 个人中心菜单
export function usePersonalMenu() {
  const { backMenu, policies } = useContext(ConfigContext);
  const [menu, setMenu] = useState<any>([]);

  const loadMenu = useCallback(async () => {
    const arr = await filterAuthMenu({ data: personalMenu, policies, backMenu });
    setMenu(arr?.[0]);
  }, [policies, backMenu]);

  useEffect(() => {
    void loadMenu();
  }, [loadMenu]);

  return menu;
}

// 顶部菜单：门户页（首页 / 产品 / 各广场及详情）展示动态产品目录菜单
export function useMenu() {
  const { pathname } = useLocation();
  const { menus: productMenu } = useProductMenu();
  if (isPortalNavPath(pathname)) return productMenu;
  return STABLE_EMPTY_MENU;
}

// 左菜单
export function useLeftMenu() {
  const { t } = useTranslation();
  const { backMenu, policies } = useContext(ConfigContext);
  const dataCenterMenu = useDataCenterMenu({ policies, backMenu });
  const tenantMenu = useTenantMenu();
  const clusterMenu = useClusterMenu({ policies, backMenu });
  const systemMenu = useSystemMenu();
  const personalMenu = usePersonalMenu();
  const path = useLocation().pathname;
  const params = useParams();

  const menu = useMemo(() => {
    let res = null;
    const effectiveCenter = getEffectiveSubCenter(path);

    if (path.startsWith('/system/')) {
      res = systemMenu;
    } else if (path.startsWith('/personal/')) {
      res = personalMenu;
    } else if (path.startsWith('/console/')) {
      // 租户：算力/AI/运维子中心统一拼装上部业务菜单 + 底部固定入口（互斥当前中心）
      if (effectiveCenter && tenantMenu?.length > 0) {
        const assembled = buildSubCenterLeftMenu(tenantMenu[0], effectiveCenter);
        if (assembled) return assembled;
      }
      res = tenantMenu[0];
    } else if (path.startsWith('/cluster/') || path.startsWith('/wsp/')) {
      res = {
        ...clusterMenu[0],
        title: params.subcluster ? t('hooks:biz.cluster') : t('hooks:cloud.management.cluster'),
      };
    } else if (path.startsWith('/data-center/') && effectiveCenter && dataCenterMenu?.length > 0) {
      // 数据中心：算力/AI/运维/运营子中心统一拼装
      const assembled = buildSubCenterLeftMenu(dataCenterMenu[0], effectiveCenter);
      if (assembled) return assembled;
      res = dataCenterMenu[0];
    }
    return { ...(res || {}) };
  }, [dataCenterMenu, tenantMenu, clusterMenu, path, params.subcluster, params.cluster, params.tenant, systemMenu, personalMenu, t]);

  return menu;
}

function hasExactMenuPath(menu: any, pathname: string): boolean {
  if (!menu) return false;
  if (menu.path === pathname) return true;
  if (!menu.children?.length) return false;
  return menu.children.some((child) => hasExactMenuPath(child, pathname));
}

/** 当前路由是否为左侧菜单注册的页面（精确匹配 path，不含详情/创建等二级页） */
export function useIsMenuPage(): boolean {
  const menu = useLeftMenu();
  const { pathname } = useLocation();

  return useMemo(() => hasExactMenuPath(menu, pathname), [menu, pathname]);
}

/** 当前路由对应的菜单 title（供 PageBanner 等使用，须在 React 组件内调用） */
export function useCurrentMenu() {
  const menu = useLeftMenu();
  const { pathname } = useLocation();

  return useMemo(() => {
    const res = pathToInfo[pathname];
    if (res) return res;
    const currMenu = findCurrMenu(menu, pathname);
    return currMenu?.title;
  }, [pathname, menu]);
}

function findCurrMenu(menu, pathname) {
  if (!menu) return null;
  if (menu.path === pathname) return menu;
  if (menu.path && pathname.includes(menu.path)) return menu;
  if (menu.children) {
    for (const child of menu.children) {
      const res = findCurrMenu(child, pathname);
      if (res) return res;
    }
  }
  return null;
}

/**
 * 在静态菜单树中按路径查找菜单项：精确匹配优先，否则取最长 path 前缀（覆盖详情/创建等二级页）。
 */
export function findMenuItemByPath(menuItems: any[] | undefined, pathname: string): any | null {
  let rawExact: any = null;
  let normalizedExact: any = null;
  let rawBestPrefix: any = null;
  let rawBestLen = -1;
  let bestPrefix: any = null;
  let bestLen = -1;
  const normalizedPathname = stripSubCenterFromPathname(pathname);

  const walk = (items: any[] | undefined) => {
    if (!items?.length) return;
    for (const item of items) {
      if (item?.path) {
        const itemPath = stripSubCenterFromPathname(item.path);
        if (item.path === pathname) {
          rawExact = item;
        } else if (pathname.startsWith(`${item.path}/`) && item.path.length > rawBestLen) {
          rawBestLen = item.path.length;
          rawBestPrefix = item;
        } else if (itemPath === normalizedPathname) {
          normalizedExact = item;
        } else if (normalizedPathname.startsWith(`${itemPath}/`)) {
          if (itemPath.length > bestLen) {
            bestLen = itemPath.length;
            bestPrefix = item;
          }
        }
      }
      if (item?.children?.length) {
        walk(item.children);
      }
    }
  };

  walk(menuItems);
  return rawExact || rawBestPrefix || normalizedExact || bestPrefix;
}

/** 数据中心静态菜单中当前路由对应的 viewAuth 列表；无则返回空数组（放行） */
export function getViewAuthsForDataCenterPath(cluster: string, pathname: string): ViewAuthTuple[] {
  const menu = getDataCenterMenu(cluster);
  // 子中心路径段不影响菜单匹配（成员等共享页会带子中心前缀）
  const item = findMenuItemByPath(menu, pathname);
  if (!item?.viewAuth) return [];
  return [item.viewAuth, ...(item.viewAuthExtra || [])];
}

/** 租户控制台静态菜单中当前路由对应的 viewAuth 列表；无则返回空数组（放行） */
export function getViewAuthsForConsolePath(tenant: string, cluster: string, pathname: string): ViewAuthTuple[] {
  const menu = getTenantMenu(tenant, cluster);
  const item = findMenuItemByPath(menu, pathname);
  if (!item?.viewAuth) return [];
  return [item.viewAuth, ...(item.viewAuthExtra || [])];
}

// 获取前端配置的所有菜单，用于后端菜单配置
export function getAllFrontMenu() {
  return [...systemMenu, ...personalMenu, ...getClusterMenu(), ...getTenantMenu('', {}), ...getDataCenterMenu('')];
}

// 获取当前模块（'data-center' | 'system' | 'console' | 'cluster'）所有权限菜单，用于权限配置
export function getModuleMenu({ module, backMenu }) {
  const menus =
    module === 'cluster'
      ? getClusterMenu()
      : module === 'console'
        ? getTenantMenu('', {})
        : module === 'system'
          ? systemMenu
          : getDataCenterMenu('');

  function getAuthMenu(menus, prefix = '') {
    if (!menus || !menus?.length) return [];
    const res = [];
    menus.forEach((i) => {
      // 排除通用隐藏项和不参与角色权限分配的菜单。
      if (i.hidden || i.permissionConfigHidden) return;
      const key = prefix ? prefix + '-' + i.title : i.title;
      const obj = {
        ...i,
        key,
        children: getAuthMenu(i.children, i.permissionChildrenKey || key),
      };
      // 父节点仍由外层 MODULES 控制；兼容叶子无需重复匹配历史配置中不存在的自身 key。
      if (backMenu.includes(key) || i.permissionVisibleWithParent) {
        res.push(obj);
      }
    });
    return res;
  }

  const moduleMenu = getAuthMenu(menus)?.[0]?.children || [];
  return moduleMenu.flatMap((item) => (item.promoteChildren ? item.children || [] : [item]));
}
